import React, { useState, useEffect } from 'react';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { studentAttendanceAPI, masterAPI } from '../services/api';
import { toast } from 'react-toastify';
import { FiCamera, FiCheck, FiX, FiClock, FiUsers, FiRefreshCw } from 'react-icons/fi';

const AttendanceScanner = () => {
  const [scanning, setScanning] = useState(false);
  const [scanner, setScanner] = useState(null);
  const [recentScans, setRecentScans] = useState([]);
  const [todayStats, setTodayStats] = useState({
    total: 0,
    present: 0,
    late: 0
  });
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedBatch, setSelectedBatch] = useState('');
  const [classes, setClasses] = useState([]);
  const [batches, setBatches] = useState([]);

  useEffect(() => {
    fetchClasses();
    fetchBatches();
    fetchTodayStats();
  }, []);

  useEffect(() => {
    return () => {
      if (scanner) {
        scanner.clear().catch(err => console.error('Error clearing scanner:', err));
      }
    };
  }, [scanner]);

  const fetchClasses = async () => {
    try {
      const response = await masterAPI.getClasses();
      if (response.data.success) {
        setClasses(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching classes:', error);
    }
  };

  const fetchBatches = async () => {
    try {
      const response = await masterAPI.getBatches();
      if (response.data.success) {
        setBatches(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching batches:', error);
    }
  };

  const fetchTodayStats = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await studentAttendanceAPI.getAttendanceReport({
        start_date: today,
        end_date: today
      });
      if (response.data.success) {
        setTodayStats({
          total: response.data.stats.total_records,
          present: response.data.stats.present,
          late: response.data.stats.late
        });
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const startScanning = async () => {
    try {
      // First, request camera permission explicitly
      const stream = await navigator.mediaDevices.getUserMedia({ video: true });
      
      // Stop the test stream immediately
      stream.getTracks().forEach(track => track.stop());
      
      // Now initialize the scanner
      const html5QrcodeScanner = new Html5QrcodeScanner(
        "qr-reader",
        { 
          fps: 10,
          qrbox: { width: 250, height: 250 },
          aspectRatio: 1.0,
          rememberLastUsedCamera: true,
          showTorchButtonIfSupported: true
        },
        false
      );

      html5QrcodeScanner.render(onScanSuccess, onScanError);
      setScanner(html5QrcodeScanner);
      setScanning(true);
      toast.success('Camera started successfully!');
    } catch (error) {
      console.error('Camera permission error:', error);
      
      if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
        toast.error('Camera permission denied. Please allow camera access in your browser settings.');
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera found on this device.');
      } else if (error.name === 'NotReadableError') {
        toast.error('Camera is already in use by another application.');
      } else if (error.name === 'SecurityError') {
        toast.error('Camera access requires HTTPS. Please use https:// or localhost.');
      } else {
        toast.error('Error accessing camera: ' + error.message);
      }
    }
  };

  const stopScanning = () => {
    if (scanner) {
      scanner.clear().then(() => {
        setScanning(false);
        setScanner(null);
      }).catch(err => {
        console.error('Error stopping scanner:', err);
      });
    }
  };

  const onScanSuccess = async (decodedText, decodedResult) => {
    try {
      // Parse QR code data
      const qrData = JSON.parse(decodedText);
      
      if (qrData.type !== 'attendance') {
        toast.error('Invalid QR code type');
        return;
      }

      // Play success sound
      playBeep();

      // Mark attendance via API
      const response = await studentAttendanceAPI.scanAttendance({
        student_id: qrData.student_id,
        date: new Date().toISOString().split('T')[0],
        status: 'present',
        class_id: selectedClass || null,
        batch_id: selectedBatch || null
      });

      if (response.data.success) {
        const studentData = response.data.data;
        
        // Add to recent scans
        setRecentScans(prev => [{
          ...studentData,
          scanned_at: new Date().toLocaleTimeString(),
          status: response.data.status
        }, ...prev.slice(0, 9)]);

        // Update stats
        fetchTodayStats();

        // Show success toast
        if (response.data.status === 'marked') {
          toast.success(`✓ ${studentData.firstname} ${studentData.lastname} - Attendance Marked`);
        } else if (response.data.status === 'already_marked') {
          toast.info(`ℹ ${studentData.firstname} ${studentData.lastname} - Already Marked Today`);
        }
      }
    } catch (error) {
      if (error instanceof SyntaxError) {
        toast.error('Invalid QR code format');
      } else {
        toast.error(error.response?.data?.message || 'Error marking attendance');
      }
      playErrorBeep();
    }
  };

  const onScanError = (errorMessage) => {
    // Ignore continuous scanning errors
  };

  const playBeep = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 800;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.1);
  };

  const playErrorBeep = () => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const oscillator = audioContext.createOscillator();
    const gainNode = audioContext.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(audioContext.destination);
    
    oscillator.frequency.value = 400;
    oscillator.type = 'sine';
    gainNode.gain.value = 0.3;
    
    oscillator.start(audioContext.currentTime);
    oscillator.stop(audioContext.currentTime + 0.2);
  };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">QR Code Scanner</h1>
          <p className="text-sm md:text-base text-gray-600 mt-1">Scan student ID cards for automatic attendance</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-blue-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Total Scans Today</p>
              <p className="text-2xl font-bold text-gray-900">{todayStats.total}</p>
            </div>
            <FiUsers className="text-blue-500" size={32} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-green-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Present</p>
              <p className="text-2xl font-bold text-gray-900">{todayStats.present}</p>
            </div>
            <FiCheck className="text-green-500" size={32} />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-yellow-500">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Late</p>
              <p className="text-2xl font-bold text-gray-900">{todayStats.late}</p>
            </div>
            <FiClock className="text-yellow-500" size={32} />
          </div>
        </div>
      </div>

      {/* Filters (Optional) */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters (Optional)</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Class Filter
            </label>
            <select
              value={selectedClass}
              onChange={(e) => setSelectedClass(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            >
              <option value="">All Classes</option>
              {classes.map(cls => (
                <option key={cls.id} value={cls.id}>{cls.class}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Batch Filter
            </label>
            <select
              value={selectedBatch}
              onChange={(e) => setSelectedBatch(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-600 focus:border-transparent"
            >
              <option value="">All Batches</option>
              {batches.map(batch => (
                <option key={batch.id} value={batch.id}>{batch.batch}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Scanner Section */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Camera Scanner</h3>
          {scanning ? (
            <button
              onClick={stopScanning}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
            >
              <FiX /> Stop Scanner
            </button>
          ) : (
            <button
              onClick={startScanning}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <FiCamera /> Start Scanner
            </button>
          )}
        </div>

        {/* Scanner Container */}
        <div id="qr-reader" className="w-full max-w-md mx-auto"></div>

        {!scanning && (
          <div className="text-center py-12">
            <FiCamera size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-600 mb-2">Click "Start Scanner" to begin scanning student ID cards</p>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4 max-w-md mx-auto text-left">
              <p className="text-sm font-semibold text-blue-900 mb-2">📷 Camera Permission Required</p>
              <ul className="text-xs text-blue-800 space-y-1">
                <li>• Your browser will ask for camera permission</li>
                <li>• Click "Allow" when prompted</li>
                <li>• If blocked, check browser address bar for camera icon</li>
                <li>• For localhost, camera should work without HTTPS</li>
              </ul>
            </div>
          </div>
        )}
      </div>

      {/* Recent Scans */}
      {recentScans.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Scans</h3>
            <button
              onClick={() => setRecentScans([])}
              className="text-sm text-gray-600 hover:text-gray-900"
            >
              Clear
            </button>
          </div>

          <div className="space-y-2">
            {recentScans.map((scan, index) => (
              <div
                key={index}
                className={`flex items-center justify-between p-3 rounded-lg border-l-4 ${
                  scan.status === 'marked' 
                    ? 'bg-green-50 border-green-500' 
                    : 'bg-blue-50 border-blue-500'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-600 text-white rounded-full flex items-center justify-center font-semibold">
                    {scan.firstname?.charAt(0)}{scan.lastname?.charAt(0)}
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">
                      {scan.firstname} {scan.lastname}
                    </p>
                    <p className="text-xs text-gray-600">
                      {scan.class_name} - {scan.batch_name} | Roll: {scan.roll_no}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-gray-500">{scan.scanned_at}</p>
                  <span className={`text-xs font-medium ${
                    scan.status === 'marked' ? 'text-green-600' : 'text-blue-600'
                  }`}>
                    {scan.status === 'marked' ? '✓ Marked' : 'ℹ Already Marked'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceScanner;
